import styled from "@emotion/styled"
import { useDealerContext } from "@porsche-customer/slfinrtl-context"
import type { InputTextChangeEventDetail } from "@porsche-design-system/components-react"
import { spacingStaticXs, spacingStaticXl } from "@porsche-design-system/components-react/emotion"
import {
	getSmartSearchResultRoute,
	inventoryListFilterValueSchema,
	isInventoryListSortingKey,
} from "@slfinrtl/ui-bff-routes"
import type { InventoryListFilterValue, InventoryListSortingKey, SortingValue } from "@slfinrtl/ui-bff-routes"
import { useCallback, useEffect, useMemo, useState } from "react"
import type { SubmitEvent } from "react"
import { FormProvider, useForm } from "react-hook-form"
import { useIntl } from "react-intl"
import { getFeatureToggle } from "src/common/getConfig"
import {
	AppTitle,
	ContentWrapper,
	Main,
	navigationMessages,
	Stage,
	StickyHeader,
	VehicleListStageList,
} from "src/components"
import { trackEvent, trackPageView } from "src/components/adobeAnalytics"
import { ErrorNotificationInline } from "src/context/errorHandler/ErrorNotificationInline"
import { withErrorHandler } from "src/context/errorHandler/withErrorHandler"
import { useModal } from "src/context/modal/useModal"
import { useBffDynamic, useConfig, useLocalStorage, useUrlState } from "src/hooks"
import { InventoryListScreenContainer } from "./InventoryListScreenContainer"
import { getDefaultSettings } from "./common/getDefaultSettings"
import { InventoryActiveFilters } from "./components/InventoryActiveFilters/InventoryActiveFilters"
import { Header } from "./components/header/Header"
import { InventoryListFilters } from "./components/inventoryListFilters/InventoryListFilters"
import { InventoryListFiltersSkeleton } from "./components/inventoryListFilters/InventoryListFiltersSkeleton"
import { useExcelExport } from "./hooks/useExcelExport/useExcelExport"
import { useInventoryListFilterView } from "./hooks/useInventoryListFilterView/useInventoryListFilterView"
import { AddVehicleModalContainer } from "./modals/addVehicleModal/AddVehicleModalContainer"
import { ExportDisclaimerModal } from "./modals/exportDisclaimerModal/ExportDisclaimerModal"
import { trackingEventMap } from "./trackingEventMap.const"

type InventoryListSettings = SortingValue<InventoryListSortingKey> & InventoryListFilterValue

export const InventoryListContainer = withErrorHandler(() => {
	const { formatMessage } = useIntl()
	const { dealershipId, dealershipInfo, groupDealershipIds, permissions } = useDealerContext()
	const isSmartSearchEnabled = getFeatureToggle("smart-search")
	const { isOneVMSMarket } = useConfig()

	const [isSmartSearchLoading, setIsSmartSearchLoading] = useState(false)
	const [isExcelLoading, setIsExcelLoading] = useState(false)
	const [smartSearchValue, setSmartSearchValue] = useState<string | undefined>()

	const [expanded, setIsExpanded] = useLocalStorage("filterExpanded", "false")
	const isExpanded = expanded === "true"

	const showAddVehicleModal = useModal(AddVehicleModalContainer)
	const showDisclaimerModal = useModal(ExportDisclaimerModal)

	const getSmartSearchResult = useBffDynamic(getSmartSearchResultRoute, "inline")

	const generateExcelExport = useExcelExport()
	const hasLocationFilter = useMemo<boolean>(
		() => (groupDealershipIds ? groupDealershipIds.length > 1 : false),
		[groupDealershipIds],
	)
	const defaultSettings = useMemo<InventoryListSettings>(
		() => getDefaultSettings(hasLocationFilter ? dealershipId : undefined),
		[dealershipId, hasLocationFilter],
	)

	const [urlState, setUrlState] = useUrlState<InventoryListSettings>(defaultSettings)

	const { filterValues, sorting } = useMemo(() => {
		const { sortBy, sortDir, ...rest } = urlState
		const validatedFilters = inventoryListFilterValueSchema.safeParse(rest)
		return {
			filterValues: validatedFilters.success ? validatedFilters.data : {},
			sorting: {
				sortBy: isInventoryListSortingKey(sortBy) ? sortBy : defaultSettings.sortBy,
				sortDir: sortDir === "ASC" || sortDir === "DESC" ? sortDir : defaultSettings.sortDir,
			},
		}
	}, [urlState, defaultSettings])

	const handleSmartSearchChange = useCallback((event: CustomEvent<InputTextChangeEventDetail>) => {
		const { value: newValue } = event.target as HTMLInputElement
		setSmartSearchValue(newValue)
	}, [])

	const filterForm = useForm<InventoryListFilterValue>({ defaultValues: filterValues })
	const { reset, subscribe } = filterForm

	useEffect(() => {
		return subscribe({ callback: (data) => setUrlState({ ...data.values, ...sorting }) })
	}, [setUrlState, sorting, subscribe])

	const { data: filterView } = useInventoryListFilterView(filterForm)

	useEffect(() => trackPageView("inventory"), [])

	const onFilterChange = useCallback(
		(
			value: InventoryListFilterValue,
			key: keyof InventoryListFilterValue,
			type?: "select" | "deselect" | "tag-dismissed" | "search",
			changedValue?: string,
		) => {
			trackEvent({
				action: type ? `${type}_${key}` : key,
				category: trackingEventMap[key],
				clickElementDetails: changedValue ?? value[key]?.toString(),
			})

			reset(value, { keepFieldsRef: true })
		},
		[reset],
	)

	const onFilterExpandedChange = useCallback(() => {
		setIsExpanded(isExpanded ? "false" : "true")
		trackEvent({
			action: isExpanded ? "show_less_filters" : "show_more_filters",
			category: "inventory",
		})
	}, [isExpanded, setIsExpanded])

	const onAddVehicleClick = useCallback(() => {
		trackEvent({ action: "add_vehicle", category: "inventory" })
		showAddVehicleModal({})
	}, [showAddVehicleModal])

	const onExcelExportClick = useCallback(() => {
		showDisclaimerModal({
			onConfirm: () => {
				setIsExcelLoading(true)
				return generateExcelExport().finally(() => setIsExcelLoading(false))
			},
		})
	}, [showDisclaimerModal, generateExcelExport])

	const onSmartSearchSubmit = useCallback(
		(event: SubmitEvent<HTMLFormElement>) => {
			event.preventDefault()
			const formData = new FormData(event.currentTarget)

			const smartSearchQuery = (formData.get("smart-search") as string).trim()
			if (!smartSearchQuery) {
				return
			}

			setIsSmartSearchLoading(true)
			getSmartSearchResult({ body: { query: smartSearchQuery }, pathParams: {}, queryParams: {} })
				.then((res) => {
					const newFilters = { ...res, dealershipIds: filterValues.dealershipIds }
					reset(newFilters, { keepFieldsRef: true })
				})
				.finally(() => setIsSmartSearchLoading(false))
		},
		[reset, getSmartSearchResult, filterValues.dealershipIds],
	)

	const smartSearch = useMemo(
		() =>
			isSmartSearchEnabled
				? {
						loading: isSmartSearchLoading,
						onChange: handleSmartSearchChange,
						onSubmit: onSmartSearchSubmit,
						value: smartSearchValue,
					}
				: undefined,
		[handleSmartSearchChange, isSmartSearchEnabled, isSmartSearchLoading, onSmartSearchSubmit, smartSearchValue],
	)

	const onSortingChange = useCallback(
		(newSorting: SortingValue<InventoryListSortingKey>) => {
			setUrlState({ ...filterValues, ...newSorting })
		},
		[filterValues, setUrlState],
	)

	return (
		<Main>
			<ContentWrapper>
				<AppTitle
					title={formatMessage(navigationMessages.inventoryList)}
					locationCode={dealershipInfo.locationCode}
				/>
				<Header
					isExcelLoading={isExcelLoading}
					addVehicleDisabledHint={
						typeof permissions.addVehicle === "string" ? permissions.addVehicle : undefined
					}
					onAddVehicleClick={permissions.addVehicle ? onAddVehicleClick : undefined}
					onExcelExportClick={onExcelExportClick}
				/>
				<ErrorNotificationInline />
			</ContentWrapper>

			<VehicleListStageList $filterScrollable $withFilters={isExpanded}>
				<Stage>
					<InventoryActiveFilters
						smartSearch={smartSearch}
						value={filterValues}
						filter={filterView}
						isExpanded={isExpanded}
						onChange={onFilterChange}
						newCarOrderIdSearchEnabled={isOneVMSMarket}
						onToggleFilters={onFilterExpandedChange}
					/>

					<StickyHeader>
						<InventoryActiveFilters
							smartSearch={smartSearch}
							filter={filterView}
							newCarOrderIdSearchEnabled={isOneVMSMarket}
							value={filterValues}
							isExpanded={isExpanded}
							onChange={onFilterChange}
							onToggleFilters={onFilterExpandedChange}
							scrollable
						/>
					</StickyHeader>
				</Stage>

				<Stage inert={!isExpanded}>
					{filterView ? (
						<FormProvider {...filterForm}>
							<InventoryListFilters value={filterValues} view={filterView} onChange={onFilterChange} />
						</FormProvider>
					) : (
						<InventoryListFiltersSkeleton hasLocationFilter={hasLocationFilter} />
					)}
				</Stage>

				<InventoryListStage>
					<FormProvider {...filterForm}>
						<InventoryListScreenContainer
							hasLocationFilter={hasLocationFilter}
							filter={filterValues}
							sorting={sorting}
							onSortingChange={onSortingChange}
						/>
					</FormProvider>
				</InventoryListStage>
			</VehicleListStageList>
		</Main>
	)
})

const InventoryListStage = styled(Stage)`
	padding: 0 0 calc(${spacingStaticXl} + ${spacingStaticXs}); /* for VehicleSharingBar not overlapping with last list item */
`
